import { useState, useEffect, useCallback } from 'react';
import { FileText, AlertTriangle, CheckCircle, TrendingDown } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';

const API = '';

export default function SEOOSContentQuality() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('admin_token');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/seoos/content-quality`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data || data.error) return <div className="text-center py-16 text-slate-500">{data?.error || 'Loading...'}</div>;

  const { overall_quality_score, match_quality_avg, bookmaker_quality_avg, risk_distribution, league_similarity, worst_pages, insights, total_analyzed } = data;

  return (
    <div className="space-y-6" data-testid="seoos-content">
      <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
        <FileText className="w-5 h-5 inline mr-2 text-purple-400" />Content Quality Engine
      </h2>

      {/* Scores */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-950/30 to-slate-900 border border-purple-500/20 rounded-xl p-5 text-center">
          <p className={`text-4xl font-black ${overall_quality_score >= 50 ? 'text-amber-400' : 'text-red-400'}`} style={{ fontFamily: 'var(--font-heading)' }}>{overall_quality_score}</p>
          <p className="text-xs text-slate-400 mt-1">Overall Quality Score</p>
        </div>
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5 text-center">
          <p className="text-3xl font-black text-white">{match_quality_avg}</p>
          <p className="text-xs text-slate-400 mt-1">Match Pages Avg</p>
        </div>
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5 text-center">
          <p className="text-3xl font-black text-white">{bookmaker_quality_avg}</p>
          <p className="text-xs text-slate-400 mt-1">Bookmaker Pages Avg</p>
        </div>
      </div>

      {/* Risk Distribution */}
      <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase mb-4">Risk Distribution ({total_analyzed} pages)</h3>
        <div className="grid grid-cols-4 gap-3">
          {Object.entries(risk_distribution || {}).map(([risk, count]) => {
            const colors = { safe: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5', moderate: 'text-amber-400 border-amber-500/20 bg-amber-500/5', high: 'text-orange-400 border-orange-500/20 bg-orange-500/5', critical: 'text-red-400 border-red-500/20 bg-red-500/5' };
            const pct = ((count / Math.max(total_analyzed, 1)) * 100).toFixed(0);
            return (
              <div key={risk} className={`border rounded-xl p-4 text-center ${colors[risk] || ''}`}>
                <p className="text-2xl font-black tabular-nums">{count}</p>
                <p className="text-[10px] uppercase font-bold">{risk} ({pct}%)</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Insights */}
      {insights?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-white uppercase">AI Quality Insights</h3>
          {insights.map((ins, i) => (
            <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <SeverityBadge severity={ins.severity} />
                <span className="text-sm font-bold text-white">{ins.title}</span>
              </div>
              <p className="text-xs text-slate-400 mb-2">{ins.detail}</p>
              <p className="text-[10px] text-purple-400">{ins.recommendation}</p>
            </div>
          ))}
        </div>
      )}

      {/* League Similarity */}
      <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase mb-4">League Template Similarity</h3>
        <div className="space-y-2">
          {Object.entries(league_similarity || {}).sort((a, b) => b[1].similarity_pct - a[1].similarity_pct).slice(0, 10).map(([lid, info]) => (
            <div key={lid} className="flex items-center gap-3">
              <span className="text-xs text-slate-300 w-40 truncate">{lid}</span>
              <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${info.similarity_pct > 70 ? 'bg-red-500' : info.similarity_pct > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${info.similarity_pct}%` }} />
              </div>
              <SeverityBadge severity={info.risk} />
              <span className="text-xs text-white font-bold w-12 text-right">{info.similarity_pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Worst Pages */}
      <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800/30">
          <h3 className="text-sm font-bold text-white uppercase">Lowest Quality Pages</h3>
        </div>
        <div className="divide-y divide-slate-800/20">
          {(worst_pages || []).slice(0, 15).map((p, i) => (
            <div key={i} className="px-4 py-2 flex items-center gap-3">
              <span className={`text-sm font-black w-8 text-center ${p.quality_score < 20 ? 'text-red-400' : p.quality_score < 40 ? 'text-orange-400' : 'text-amber-400'}`}>{p.quality_score}</span>
              <SeverityBadge severity={p.risk} />
              <span className="text-xs text-slate-300 flex-1 truncate">{p.slug}</span>
              <span className="text-[10px] text-slate-500">{p.markets} mkts</span>
              <span className="text-[10px] text-slate-600">{p.reasons?.[0]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
